import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Box, Typography, Card, CardHeader, CardContent, CardMedia, Avatar, Pagination, OutlinedInput,
    InputLabel, InputAdornment, FormControl
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useSnackbar } from 'notistack';
import SearchIcon from '@mui/icons-material/Search';
import logo from '~/assets/images/loGo.png';

import ArticleServices from '../services/ArticleServices';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

function formatDate(dateInput, formatType = 'dd/mm/yyyy') {
    if (!dateInput) return "";

    const date = new Date(dateInput);
    if (isNaN(date)) return "";

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    if (formatType === 'yyyy-mm-dd') {
        return `${year}-${month}-${day}`;
    } else if (formatType === 'dd/mm/yyyy') {
        return `${day}/${month}/${year}`;
    } else {
        throw new Error("formatType must be 'dd/mm/yyyy' or 'yyyy-mm-dd'");
    }
}

const ListArticle = ({ category = "" }) => {
    const [page, setPage] = useState(1);
    const [totalPage, setTotalPage] = useState(0);
    const [listArticle, setListArticle] = useState([]);
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 500);

    const { enqueueSnackbar } = useSnackbar();
    const showAlert = useCallback((message, variant) => {
        enqueueSnackbar(message, { variant });
    }, [enqueueSnackbar]);

    const getArticle = useCallback(async () => {
        try {
            let response = await ArticleServices.getByCategory(debouncedSearchText, category, true, 12, page);
            if (response) {
                setListArticle(response.items);
                setTotalPage(Math.ceil(Number(response.totalCount) / Number(response.pageSize)));
            }
        } catch (error) {
            showAlert("Lỗi khi tải danh sách bài viết!", "error");
            console.log(error);
        }
    }, [debouncedSearchText, category, page, showAlert]);

    useEffect(() => {
        getArticle();
    }, [getArticle]);

    const handleChangeSearch = (event) => {
        setSearchText(event.target.value);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
    };

    return (
        <Box sx={{ maxWidth: 'lg', marginInline: 'auto', marginBlock: 6, p: 2 }}>
            <FormControl sx={{ mb: 4, width: { md: '50%', xs: '100%' }, ml: "50%", transform: "translateX(-50%)" }} variant="outlined">
                <InputLabel htmlFor="search">Tìm kiếm</InputLabel>
                <OutlinedInput
                    id="search"
                    value={searchText}
                    onChange={handleChangeSearch}
                    endAdornment={
                        <InputAdornment position="end"> <SearchIcon /></InputAdornment>
                    }
                    label="Tìm kiếm"
                />
            </FormControl>
            <Grid container spacing={2}>
                {listArticle.map((article, index) => (
                    <Grid key={index} size={{ md: 4, sm: 6, xs: 12 }}>
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                height: 450,
                                transition: "box-shadow 0.5s",
                                "&:hover": {
                                    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
                                }
                            }}>
                            <CardHeader
                                avatar={<Avatar src={logo} aria-label="recipe" />}
                                title={
                                    <Typography sx={{ fontWeight: 'bold', color: 'var(--primary)' }}>
                                        Trung tâm ngoại ngữ và tin học
                                    </Typography>
                                }
                                subheader={
                                    <Box sx={{ display: 'flex', columnGap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                                        <Typography variant="body2">
                                            Ngày đăng: {formatDate(article.createdAt)}
                                        </Typography>
                                        <Typography variant="body2">
                                            Lượt xem: {article.view}
                                        </Typography>
                                    </Box>
                                }
                            />
                            <CardMedia
                                sx={{ height: 240 }}
                                image={process.env.REACT_APP_API_BASE_URL + article.image}
                                alt={article.name} />
                            <CardContent>
                                <Link
                                    to={`/article-detail/${article.id}`}
                                    style={{ textDecoration: "none", color: "var(--primary)" }}
                                    className='link-blue'
                                    onClick={() => window.scrollTo({ top: 0 })}>
                                    <Typography
                                        sx={{
                                            fontWeight: 'bold',
                                            fontSize: 20,
                                            textAlign: 'center',
                                            display: '-webkit-box',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 2,
                                            overflow: 'hidden',
                                        }}>
                                        {article.name.toUpperCase()}
                                    </Typography>
                                </Link>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            {totalPage > 1 && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination count={totalPage} page={page} size="large" shape="rounded" color="primary" onChange={handlePageChange} />
                </Box>
            )}
        </Box>
    );
}

export default ListArticle;
